// Browsers word a missing-chunk failure differently, and a SPA-fallback server answers
// with the HTML shell instead of a 404, so match every known shape.
const STALE_BUILD_ERROR =
  /Loading chunk|Loading CSS chunk|ChunkLoadError|Unexpected token '<'|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

// webpack 5 appends the failing URL to the message: "Loading chunk 42 failed. (error: <url>)".
const ASSET_URL_IN_MESSAGE = /\(error:\s*(\S+?)\)/;

const RELOAD_TIMESTAMP_KEY = 'lb-chunk-reload-at';
const RELOAD_ATTEMPTS_KEY = 'lb-chunk-reload-attempts';

// Guards against a reload loop when the fresh build is itself broken.
const RELOAD_COOLDOWN_MS = 30_000;
const MAX_RELOAD_ATTEMPTS = 3;

// A stalled network must not stall the recovery: the reload matters more than the eviction.
const EVICTION_TIMEOUT_MS = 4_000;

const readMessageAndName = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (error !== null && typeof error === 'object') {
    const { name, message } = error as { name?: unknown; message?: unknown };
    return `${typeof name === 'string' ? name : ''} ${typeof message === 'string' ? message : ''}`;
  }

  return '';
};

export const isStaleBuildError = (error: unknown): boolean => STALE_BUILD_ERROR.test(readMessageAndName(error));

export const extractAssetUrl = (error: unknown): string | null =>
  ASSET_URL_IN_MESSAGE.exec(readMessageAndName(error))?.[1] ?? null;

// sessionStorage throws in Safari private mode.
const readNumber = (key: string): number => {
  try {
    return Number(window.sessionStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
};

const markReloadAttempt = (): void => {
  try {
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
    window.sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, String(readNumber(RELOAD_ATTEMPTS_KEY) + 1));
  } catch {
    // Best-effort: the loop guard is optional.
  }
};

export const clearStaleBuildReloadGuard = (): void => {
  try {
    window.sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
    window.sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);
  } catch {
    // Best-effort: the loop guard is optional.
  }
};

const canReload = (): boolean =>
  readNumber(RELOAD_ATTEMPTS_KEY) < MAX_RELOAD_ATTEMPTS &&
  Date.now() - readNumber(RELOAD_TIMESTAMP_KEY) > RELOAD_COOLDOWN_MS;

// `cache: 'reload'` skips the cache on the way out and REPLACES the stored entry with the
// response. That is what frees a browser still holding a long-lived 404 for this URL, which
// a plain reload cannot do on its own.
const evictFromHttpCache = async (url: string): Promise<void> => {
  const abort = new AbortController();
  const timeout = window.setTimeout(() => abort.abort(), EVICTION_TIMEOUT_MS);

  try {
    await fetch(url, { cache: 'reload', credentials: 'same-origin', signal: abort.signal });
  } catch {
    // The reload below is the actual recovery; a failed eviction only makes it less likely to work.
  } finally {
    window.clearTimeout(timeout);
  }
};

// Both the capture-phase error listener and the unhandledrejection fire for a single failed
// chunk, so the first one through owns the recovery.
let isRecovering = false;

/**
 * Recovers from a chunk the running build can no longer load, by dropping the failed URL from
 * the HTTP cache and reloading. Does nothing once the loop guard trips, which leaves the
 * caller's error UI on screen rather than reloading forever.
 */
export const recoverFromStaleBuild = async (assetUrl?: string | null): Promise<void> => {
  if (isRecovering || !canReload()) {
    return;
  }

  isRecovering = true;
  markReloadAttempt();

  const urls = assetUrl ? [assetUrl, '/index.html'] : ['/index.html'];
  await Promise.all(urls.map(evictFromHttpCache));

  window.location.reload();
};

// Same-origin build assets only: reloading on any failed resource (a broken image, a blocked
// third-party script) would put the app in a reload loop.
export const isBuildAssetUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }

  try {
    const { origin, pathname } = new URL(url, window.location.href);
    return (
      origin === window.location.origin && (pathname.startsWith('/static/js/') || pathname.startsWith('/static/css/'))
    );
  } catch {
    return false;
  }
};

/**
 * URL of the build asset whose load failed, given the target of a capture-phase error event.
 * Null for every other event target, which is what keeps the listener from reloading on an
 * unrelated resource failure.
 */
export const failedBuildAssetUrl = (target: EventTarget | null): string | null => {
  if (target instanceof HTMLScriptElement) {
    return isBuildAssetUrl(target.src) ? target.src : null;
  }

  if (target instanceof HTMLLinkElement) {
    return isBuildAssetUrl(target.href) ? target.href : null;
  }

  return null;
};
